import {
  ActionStatusEnum,
  ReminderReminderTypeEnum,
} from "@clients/velys-infrastructure/api";

export type ActionType = "broadcast" | "action-item" | "event" | "assessment";

export type ICarePlanAction = IActionItem &
  IEvent &
  IBroadcast &
  IAssessment & { type?: string };

export interface ICategorizedActions {
  preop: ICarePlanAction[];
  postop: ICarePlanAction[];
}

export interface ICategorizedBroadcasts {
  preop: IBroadcast[];
  postop: IBroadcast[];
}

export interface ICategorizedActionItems {
  preop: IActionItem[];
  postop: IActionItem[];
}

export interface ICategorizedEvents {
  preop: IEvent[];
  postop: IEvent[];
}

export interface ICategorizedItemsList {
  exercises: ICategorizedBroadcasts;
  education: ICategorizedBroadcasts;
  actions: ICategorizedActionItems;
  events: ICategorizedEvents;
  [key: string]:
    | ICategorizedActions
    | ICategorizedBroadcasts
    | ICategorizedActionItems
    | ICategorizedEvents;
}

export interface ICarePlan {
  id: string;
  name: string;
  // actions: ICarePlanAction[];
}

export interface IActionReminder {
  id?: string;
  message: string; // sms or email
  execution_date?: string; // time of day HH:mm
  always_send: boolean;
  days_before_due_to_remind: number; // days before due to remind
  reminder_type?: ReminderReminderTypeEnum; // sms or email
  reminder_repeat_interval_days?: number;
  destination?: string; // phone or email
}

export interface IAssessment {
  id?: string;
  title: string;
  description?: string;
  type?: string;
  uri?: string;
  slug?: string;
  delta_days_from_surgery: string;
  performance_window?: {
    days_pre: string;
    days_post: string;
  };
  reminders?: IActionReminder[];
  required?: boolean;
  status?: string;
}

export type IAssesmentPlan = {
  id: string;
  title: string;
  name: string;
  provider: string;
  uri?: string;
};

export interface IEvent {
  id?: string;
  title: string;
  description?: string;
  delta_days_from_surgery: string;
  reminders?: IActionReminder[];
  event_date?: string;
  execution_date?: string;
  status?: ActionStatusEnum;
}

export interface IBroadcast {
  id?: string;
  title: string;
  category?: string; // e.g. "Exercise" | "Education"
  description?: string;
  content?: string;
  asset?: IAsset;
  assets?: IAsset[];
  delta_days_from_surgery: string;
  reminders?: IActionReminder[];
  execution_date?: string;
  status?: ActionStatusEnum;
}

export interface IActionItem {
  id?: string;
  title: string;
  description?: string;
  description_long?: string;
  status?: ActionStatusEnum; // e.g. "action_completed"
  delta_days_from_surgery: string;
  reminders?: IActionReminder[];
  assets?: IAsset[];
  required?: boolean; // notification requirement
  execution_date?: string;
  source?: string;
}

export interface IAsset {
  id?: string;
  asset_id?: string;
  name?: string;
  slug?: string;
  url?: string;
  asset_type?: string;
  s3_path?: string;
  description?: string;
  created_at?: string;
}

export type ITags = string[];

export interface ICarePlanTemplate {
  care_plan_name: string;
  care_plan_id?: string;
  version?: string;
  language?: string;
  tags?: ITags;
  create_date?: string;
  surgery_date: string;
  status?: string;
  info?: {
    office: {
      name: string;
      phone: string;
      location: string;
    };
    physician: {
      name: string;
      npi: string;
      email: string;
    };
  };
  patient_id?: string;
  first_name?: string;
  last_name?: string;
  email: string;
  phone_number?: string;
  timezone: string;
  actions: IActionItem[];
  assessments: IAssessment[];
  broadcasts: IBroadcast[];
  assessment_plans: IAssesmentPlan[];
  events: IEvent[];
  assets?: IAsset[];
}

export const carePlanTemplateList: ICarePlan[] = [
  {
    id: "care-plan-1",
    name: "Total Hip Workflow",
  },
  {
    id: "care-plan-2",
    name: "Total Left Knee Workflow",
  },
  {
    id: "care-plan-3",
    name: "Total Right Knee Workflow",
  },
  {
    id: "care-plan-4",
    name: "Pre/Post Assessment Workflow",
  },
];

export const carePlanTemplate: ICarePlanTemplate = {
  care_plan_name: "Knee Recovery Plan",
  care_plan_id: "careplan-001",
  version: "1.0",
  language: "en",
  tags: ["orthopedics", "knee", "post-op"],
  create_date: "2025-09-23T14:00:00Z",
  surgery_date: "2025-10-11T09:00:00Z",
  status: "ACTIVE",
  info: {
    office: {
      name: "Downtown Physical Therapy",
      phone: "+15559876543",
      location: "123 Main St, Cityville, NY",
    },
    physician: {
      name: "Dr. John Smith",
      npi: "1234567890",
      email: "dr.smith@example.com",
    },
  },
  first_name: "",
  last_name: "",
  email: "jane.doe@example.com",
  phone_number: "+15551234567",
  timezone: "America/New_York",
  assessment_plans: [
    {
      id: "test_plan",
      name: "Test Plan",
      provider: "mock",
      title: "test title",
      uri: "testing_plans",
    },
  ],
  actions: [
    {
      id: "action-101",
      title: "Post-Surgery Mobility Check",
      description: "Initial mobility assessment after surgery",
      description_long: "A series of guided movements to assess hip mobility.",
      status: "completed",
      delta_days_from_surgery: "1",
      reminders: [
        {
          id: "reminder-1",
          message: "Don't forget to complete your mobility check today.",
          execution_date: "09:00",
          always_send: true,
          reminder_type: "sms",
          destination: "+15551234567",
          days_before_due_to_remind: 0,
        },
      ],
      assets: [
        {
          id: "asset-102",
          name: "Recovery Stretch Video",
          slug: "recovery-stretch-video",
          asset_type: "video",
          url: "https://example.com/recovery-stretch",
          description: "Gentle stretching routine for recovery.",
        },
      ],
      required: true,
    },
    {
      id: "action-102",
      title: "Pre-Surgery Mobility Check",
      description: "Initial mobility assessment after surgery",
      description_long: "A series of guided movements to assess hip mobility.",
      status: "completed",
      delta_days_from_surgery: "-2",
      reminders: [
        {
          id: "reminder-1",
          message: "Don't forget to complete your mobility check today.",
          execution_date: "09:00",
          always_send: true,
          reminder_type: "sms",
          destination: "+15551234567",
          days_before_due_to_remind: 0,
        },
      ],
      required: true,
    },
    {
      id: "action-103",
      title: "Pre-Surgery Mobility Check 1",
      description: "Initial mobility assessment after surgery",
      description_long: "A series of guided movements to assess hip mobility.",
      status: "completed",
      delta_days_from_surgery: "-10",
      reminders: [
        {
          id: "reminder-1",
          message: "Don't forget to complete your mobility check today.",
          execution_date: "09:00",
          always_send: true,
          reminder_type: "sms",
          destination: "+15551234567",
          days_before_due_to_remind: 0,
        },
      ],
      assets: [
        {
          id: "asset-102",
          name: "Recovery Stretch Video",
          slug: "recovery-stretch-video",
          asset_type: "video",
          url: "https://example.com/recovery-stretch",
          description: "Gentle stretching routine for recovery.",
        },
      ],
      required: true,
    },
    {
      id: "action-104",
      title: "Post-Op Upload Photo",
      description: "Initial mobility assessment after surgery",
      description_long: "A series of guided movements to assess hip mobility.",
      status: "completed",
      delta_days_from_surgery: "5",
      reminders: [
        {
          id: "reminder-1",
          message: "Don't forget to complete your mobility check today.",
          execution_date: "09:00",
          always_send: true,
          reminder_type: "sms",
          destination: "+15551234567",
          days_before_due_to_remind: 0,
        },
      ],
      required: true,
    },
    {
      id: "action-105",
      title: "Post-Op Upload Photo 4",
      description: "Initial mobility assessment after surgery",
      description_long: "A series of guided movements to assess hip mobility.",
      status: "completed",
      delta_days_from_surgery: "10",
      reminders: [
        {
          id: "reminder-1",
          message: "Don't forget to complete your mobility check today.",
          execution_date: "09:00",
          always_send: true,
          reminder_type: "sms",
          destination: "+15551234567",
          days_before_due_to_remind: 0,
        },
      ],
      required: true,
    },
  ],
  assessments: [
    {
      id: "assessment-301",
      title: "Hip Flexibility Survey",
      description: "Short questionnaire on joint flexibility",
      type: "survey",
      uri: "https://example.com/assessments/hip-flexibility",
      slug: "hip-flexibility",
      delta_days_from_surgery: "-3",
      performance_window: {
        days_pre: "0",
        days_post: "7",
      },
      reminders: [
        {
          id: "reminder-1",
          message: "Your flexibility survey is due today.",
          execution_date: "08:00",
          reminder_type: "email",
          destination: "patient@example.com",
          always_send: false,
          days_before_due_to_remind: 0,
        },
      ],
      required: true,
    },
  ],
  assets: [
    {
      id: "asset-101",
      name: "Hip Warning",
      slug: "hip-warning",
      asset_type: "message",
      description: "Move slowly w/ caution",
    },
    {
      id: "asset-102",
      name: "Recovery Stretch Video",
      slug: "recovery-stretch-video",
      asset_type: "video",
      url: "https://example.com/recovery-stretch.mp4",
      description: "Gentle stretching routine for recovery.",
    },
  ],
  broadcasts: [
    {
      id: "broadcast-501",
      title: "Broadcast 123",
      category: "Exercise",
      description: "Daily recovery instructions and exercise video",
      asset: {
        id: "asset-101",
        slug: "hip-warning",
        asset_type: "pdf",
        description: "Move slowly w/ caution",
        s3_path: "/pdfs/VELYSExpertInsights BestPractices.pdf",
      },
      delta_days_from_surgery: "-3",
      reminders: [
        {
          id: "reminder-1",
          message: "Here’s your recovery plan for today.",
          execution_date: "08:00",
          reminder_type: "email",
          destination: "patient@example.com",
          always_send: false,
          days_before_due_to_remind: 0,
        },
      ],
    },
    {
      id: "broadcast-502",
      title: "Post-Op Exercise",
      category: "Exercise",
      description: "Daily recovery instructions and exercise video",
      asset: {
        id: "asset-101",
        slug: "hip-warning",
        asset_type: "png",
        description: "Move slowly w/ caution",
        s3_path: "/J&JMedTech.png",
      },
      delta_days_from_surgery: "10",
      reminders: [
        {
          id: "reminder-1",
          message: "Here’s your recovery plan for today.",
          execution_date: "08:00",
          reminder_type: "email",
          destination: "patient@example.com",
          always_send: false,
          days_before_due_to_remind: 0,
        },
      ],
    },
    {
      id: "broadcast-503",
      title: "Broadcast Video",
      category: "Exercise",
      description: "Daily recovery instructions and exercise video",
      asset: {
        id: "asset-101",
        slug: "hip-warning",
        asset_type: "video",
        description: "Move slowly w/ caution",
        url: "https://www.youtube.com/embed/hNCqIgl7Pjc",
      },
      delta_days_from_surgery: "-3",
      reminders: [
        {
          id: "reminder-1",
          message: "Here’s your recovery plan for today.",
          execution_date: "08:00",
          reminder_type: "email",
          destination: "patient@example.com",
          always_send: false,
          days_before_due_to_remind: 0,
        },
      ],
    },
  ],
  events: [
    {
      id: "event-401",
      title: "Follow-Up Appointment",
      description: "Check-in with your orthopedic surgeon",
      delta_days_from_surgery: "14",
      reminders: [
        {
          id: "reminder-1",
          message: "Your follow-up appointment is tomorrow at 10 AM.",
          execution_date: "09:00",
          reminder_type: "sms",
          destination: "+15559876543",
          always_send: false,
          days_before_due_to_remind: 0,
        },
      ],
    },
    {
      id: "event-402",
      title: "Pre-Op Appointment",
      description: "Check-in with your orthopedic surgeon",
      delta_days_from_surgery: "-14",
      reminders: [
        {
          id: "reminder-1",
          message: "You pre-op appointment is tomorrow at 10 AM.",
          execution_date: "09:00",
          reminder_type: "sms",
          destination: "+15559876543",
          always_send: false,
          days_before_due_to_remind: 0,
        },
      ],
    },
  ],
};

export const carePlanTemplates = [carePlanTemplate];

export const predefinedActionItems: IActionItem[] = [
  {
    id: "predefined-1",
    title: "Stop Smoking",
    description: "Initial mobility assessment after surgery",
    description_long: "A series of guided movements to assess hip mobility.",
    status: "completed",
    delta_days_from_surgery: "5",
    reminders: [
      {
        id: "reminder-1",
        message: "Don't forget to complete your mobility check today.",
        execution_date: "09:00",
        always_send: true,
        reminder_type: "sms",
        destination: "+15551234567",
        days_before_due_to_remind: 0,
      },
    ],
    required: true,
  },
  {
    id: "predefined-2",
    title: "Check Blood Pressure",
    description: "Initial mobility assessment after surgery",
    description_long: "A series of guided movements to assess hip mobility.",
    status: "completed",
    delta_days_from_surgery: "-2",
    reminders: [
      {
        id: "reminder-1",
        message: "Don't forget to complete your mobility check today.",
        execution_date: "09:00",
        always_send: true,
        reminder_type: "sms",
        destination: "+15551234567",
        days_before_due_to_remind: 0,
      },
    ],
    required: true,
  },
  {
    id: "predefined-3",
    title: "Post-Surgery Mobility Check",
    description: "Initial mobility assessment after surgery",
    description_long: "A series of guided movements to assess hip mobility.",
    status: "completed",
    delta_days_from_surgery: "1",
    reminders: [
      {
        id: "reminder-1",
        message: "Don't forget to complete your mobility check today.",
        execution_date: "09:00",
        always_send: true,
        reminder_type: "sms",
        destination: "+15551234567",
        days_before_due_to_remind: 0,
      },
    ],
    required: true,
  },
];

export const scriptCareplan = {
  care_plan_name: "Shoulder Recovery Plan",
  care_plan_id: "0199fd03-63ab-776d-a7f0-ac685f2ed823",
  version: "1.0",
  language: null,
  tags: null,
  create_date: "2025-10-19T15:08:02Z",
  surgery_date: "2025-12-03T09:00:00Z",
  status: "ACTIVE",
  info: null,
  patient_id: "3428c4a8-5021-70d2-4502-1a1da2d0df6d",
  first_name: "Luca",
  last_name: "Santarella",
  email: "luca+2025101902@docspera.com",
  phone_number: null,
  timezone: "America/New_York",
  actions: [
    {
      id: "0199fd03-bed8-7816-a942-427049247158",
      title:
        "Confirm with your surgeon if any specialist clearances need to be obtained",
      description:
        "Confirm with your surgeon if any specialist clearances need to be obtained (e.g., cardiologist, pulmonologist, dentist) and schedule those appointments. Make sure clearance appointments are scheduled within the time frame recommended by your care team before surgery.",
      description_long:
        "Confirm with your surgeon if any specialist clearances need to be obtained (e.g., cardiologist, pulmonologist, dentist) and schedule those appointments. Make sure clearance appointments are scheduled within the time frame recommended by your care team before surgery.",
      status: "pending",
      delta_days_from_surgery: "-34",
      reminders: [
        {
          message:
            "Schedule specialist clearance appointments your surgeon recommends and mark this action item complete after attending the required appointment(s).",
          execution_date: "13:00",
          always_send: false,
          days_before_due_to_remind: 0,
          reminder_type: "email",
          reminder_repeat_interval_days: 0,
          destination: "luca+2025101902@docspera.com",
        },
      ],
      assets: "",
      required: false,
      execution_date: "2025-10-30T09:00:00Z",
    },
    {
      id: "0199fd03-bed9-7513-8129-c8d53298a1f1",
      title:
        "Know when to stop taking blood thinners, over the counter medications, vitamins and supplements",
      description:
        "If you take any blood thinners, over the counter medications, vitamins or supplements, make sure you know when to stop taking them before surgery. If you are unsure, contact your surgeon's office. Your provider should instruct you on when to resume these medications.",
      description_long:
        "If you take any blood thinners, over the counter medications, vitamins or supplements, make sure you know when to stop taking them before surgery. If you are unsure, contact your surgeon's office. Your provider should instruct you on when to resume these medications.",
      status: "pending",
      delta_days_from_surgery: "-12",
      reminders: [
        {
          message:
            "Ask your provider when to stop taking blood thinners and over the counter medications. When understood, mark this action item complete.",
          execution_date: "13:00",
          always_send: false,
          days_before_due_to_remind: 0,
          reminder_type: "email",
          reminder_repeat_interval_days: 0,
          destination: "luca+2025101902@docspera.com",
        },
      ],
      assets: "",
      required: false,
      execution_date: "2025-11-21T09:00:00Z",
    },
    {
      id: "0199fd03-beda-7e42-81b3-d5388f98a3fc",
      title:
        "Leave your surgical dressing in place unless otherwise instructed by your provider",
      description:
        "Leave your surgical dressing in place unless otherwise instructed by your provider. If you have any concerns, contact your provider's office or send an incision photo from the home screen.",
      description_long:
        "Leave your surgical dressing in place unless otherwise instructed by your provider. If you have any concerns, contact your provider's office or send an incision photo from the home screen.",
      status: "pending",
      delta_days_from_surgery: "3",
      reminders: [
        {
          message:
            "Keep the surgical dressing in place unless your provider instructed otherwise. If concerned, contact your care team, otherwise mark this action item complete.",
          execution_date: "13:00",
          always_send: false,
          days_before_due_to_remind: 0,
          reminder_type: "email",
          reminder_repeat_interval_days: 0,
          destination: "luca+2025101902@docspera.com",
        },
      ],
      assets: "",
      required: false,
      execution_date: "2025-12-06T09:00:00Z",
    },
    {
      id: "0199fd03-bedb-7d4e-8e5a-1378997d4d7a",
      title: "Ask your provider when to resume medications",
      description:
        "Make sure you understand when you can resume any medications that were stopped prior to surgery. If you are unsure, check with the prescribing provider.",
      description_long:
        "Make sure you understand when you can resume any medications that were stopped prior to surgery. If you are unsure, check with the prescribing provider.",
      status: "pending",
      delta_days_from_surgery: "5",
      reminders: [
        {
          message:
            "Ask your prescribing provider about when to resume any medications that were stopped prior to surgery. When understood, mark this action item complete.",
          execution_date: "11:00",
          always_send: false,
          days_before_due_to_remind: 0,
          reminder_type: "email",
          reminder_repeat_interval_days: 0,
          destination: "luca+2025101902@docspera.com",
        },
      ],
      assets: "",
      required: false,
      execution_date: "2025-12-08T09:00:00Z",
    },
    {
      id: "0199fd03-bedc-7276-903a-44fd4d056108",
      title:
        "Follow your provider's instructions on when to stop taking any medications prescribed after surgery",
      description:
        "Make sure you understand when you should stop taking any medications that were prescribed to you after surgery. If you are unsure, reach out to your provider's office.",
      description_long:
        "Make sure you understand when you should stop taking any medications that were prescribed to you after surgery. If you are unsure, reach out to your provider's office.",
      status: "pending",
      delta_days_from_surgery: "30",
      reminders: [
        {
          message:
            "Make sure you know when to stop taking medications prescribed for your surgery. If unsure, ask your provider. When understood, mark this action item complete.",
          execution_date: "8:00",
          always_send: false,
          days_before_due_to_remind: 0,
          reminder_type: "email",
          reminder_repeat_interval_days: 0,
          destination: "luca+2025101902@docspera.com",
        },
      ],
      assets: "",
      required: false,
      execution_date: "2026-01-02T09:00:00Z",
    },
  ],
  assessments: [],
  broadcasts: [
    {
      id: "0199fd03-bee2-72df-acbc-f6d99e6eebd1",
      title: "Diabetes and Surgery",
      category: "Education",
      description:
        "Considerations for a diabetic patient before and after surgery",
      asset: {
        id: "0199fd02-8ec8-7e92-ab59-3571c3619c33",
        slug: "shoulder-diabetes-and-surgery",
        url: "s3://etziints-velys-sit-assets/assets/92bb0ce8-d16d-4837-a3e7-a3cb1b2568fa/0199fd02-8ec8-7e92-ab59-3571c3619c33/shoulder-diabetes-and-surgery.file",
        description: "Asset for Shoulder Recovery Plan",
        asset_type: "file",
        name: "Shoulder_Diabetes and Surgery",
        s3_path:
          "s3://etziints-velys-sit-assets/assets/92bb0ce8-d16d-4837-a3e7-a3cb1b2568fa/0199fd02-8ec8-7e92-ab59-3571c3619c33/shoulder-diabetes-and-surgery.file",
        created_at: "2025-10-19T15:07:07.495102Z",
      },
      delta_days_from_surgery: "-36",
      execution_date: "2025-10-28T09:00:00Z",
      reminders: [],
      status: "pending",
    },
    {
      id: "0199fd03-bee3-7c49-abc8-e056420019c3",
      title: "Your Pre-Op Exercises",
      category: "Education",
      description:
        "Common exercises used for improving shoulder mobility and strength",
      asset: {
        id: "0199fd02-8811-7dad-825c-d61475fd2fd5",
        slug: "shoulder-your-pre-op-exercises",
        url: "s3://etziints-velys-sit-assets/assets/92bb0ce8-d16d-4837-a3e7-a3cb1b2568fa/0199fd02-8811-7dad-825c-d61475fd2fd5/shoulder-your-pre-op-exercises.file",
        description: "Asset for Shoulder Recovery Plan",
        asset_type: "file",
        name: "Shoulder_Your Pre-Op Exercises",
        s3_path:
          "s3://etziints-velys-sit-assets/assets/92bb0ce8-d16d-4837-a3e7-a3cb1b2568fa/0199fd02-8811-7dad-825c-d61475fd2fd5/shoulder-your-pre-op-exercises.file",
        created_at: "2025-10-19T15:07:06.747945Z",
      },
      delta_days_from_surgery: "-32",
      execution_date: "2025-11-01T09:00:00Z",
      reminders: [],
      status: "pending",
    },
    {
      id: "0199fd03-bee4-794f-aafd-dedf8891afe7",
      title: "Managing Pain After Surgery",
      category: "Education",
      description: "Tips for easing surgical discomfort",
      asset: {
        id: "0199fd02-8fa7-721b-acf5-8ff372936827",
        slug: "shoulder-managing-pain-after-surgery",
        url: "s3://etziints-velys-sit-assets/assets/92bb0ce8-d16d-4837-a3e7-a3cb1b2568fa/0199fd02-8fa7-721b-acf5-8ff372936827/shoulder-managing-pain-after-surgery.file",
        description: "Asset for Shoulder Recovery Plan",
        asset_type: "file",
        name: "Shoulder_Managing Pain After Surgery",
        s3_path:
          "s3://etziints-velys-sit-assets/assets/92bb0ce8-d16d-4837-a3e7-a3cb1b2568fa/0199fd02-8fa7-721b-acf5-8ff372936827/shoulder-managing-pain-after-surgery.file",
        created_at: "2025-10-19T15:07:07.707621Z",
      },
      delta_days_from_surgery: "4",
      execution_date: "2025-12-07T09:00:00Z",
      reminders: [
        {
          message: "Make sure to review this week's education content.",
          execution_date: "13:00",
          always_send: false,
          days_before_due_to_remind: 0,
          reminder_type: "email",
          reminder_repeat_interval_days: 0,
          destination: "luca+2025101902@docspera.com",
        },
      ],
      status: "pending",
    },
    {
      id: "0199fd03-bee5-7edc-8600-2f9875c00fec",
      title: "Returning to Independence",
      category: "Education",
      description: "What to expect in the weeks following surgery",
      asset: {
        id: "0199fd02-8d91-7924-816d-a4bdfc387ef0",
        slug: "shoulder-returning-to-independence",
        url: "s3://etziints-velys-sit-assets/assets/92bb0ce8-d16d-4837-a3e7-a3cb1b2568fa/0199fd02-8d91-7924-816d-a4bdfc387ef0/shoulder-returning-to-independence.file",
        description: "Asset for Shoulder Recovery Plan",
        asset_type: "file",
        name: "Shoulder_Returning to Independence",
        s3_path:
          "s3://etziints-velys-sit-assets/assets/92bb0ce8-d16d-4837-a3e7-a3cb1b2568fa/0199fd02-8d91-7924-816d-a4bdfc387ef0/shoulder-returning-to-independence.file",
        created_at: "2025-10-19T15:07:07.276915Z",
      },
      delta_days_from_surgery: "13",
      execution_date: "2025-12-16T09:00:00Z",
      reminders: [
        {
          message: "Make sure to review this week's education content.",
          execution_date: "13:00",
          always_send: false,
          days_before_due_to_remind: 0,
          reminder_type: "email",
          reminder_repeat_interval_days: 0,
          destination: "luca+2025101902@docspera.com",
        },
      ],
      status: "pending",
    },
  ],
  events: [
    {
      id: "0199fd03-bedd-7536-acfd-0fca3b69518d",
      title: "Surgery",
      description: "",
      delta_days_from_surgery: "0",
      reminders: [],
      event_date: "",
      execution_date: "2025-12-03T09:00:00Z",
      status: "pending",
    },
    {
      id: "0199fd03-bee1-77e3-9a01-f6dd5efc5ef9",
      title: "TSA",
      description: "",
      delta_days_from_surgery: "0",
      reminders: [],
      event_date: "2025-12-03T09:00:00Z",
      execution_date: "2025-12-03T09:00:00Z",
      status: "pending",
    },
  ],
};

export const exmpleActions = [
  {
    action_id: "0199fd03-bed8-7816-a942-427049247158",
    enrollment_id: "0199fd03-63ab-776d-a7f0-ac685f2ed823",
    patient_id: "3428c4a8-5021-70d2-4502-1a1da2d0df6d",
    organization_id: "92bb0ce8-d16d-4837-a3e7-a3cb1b2568fa",
    execution_arn: "",
    schedule_arn: "",
    title:
      "Confirm with your surgeon if any specialist clearances need to be obtained",
    description:
      "Confirm with your surgeon if any specialist clearances need to be obtained (e.g., cardiologist, pulmonologist, dentist) and schedule those appointments. Make sure clearance appointments are scheduled within the time frame recommended by your care team before surgery.",
    delta_days_from_surgery: -34,
    uri: "velys://patientpath/orgs/92bb0ce8-d16d-4837-a3e7-a3cb1b2568fa/enrollments/0199fd03-63ab-776d-a7f0-ac685f2ed823/actions/0199fd03-bed8-7816-a942-427049247158",
    slug: "ACTION",
    required: false,
    status: "pending",
    completed_at: null,
    reminders: [
      {
        message:
          "Schedule specialist clearance appointments your surgeon recommends and mark this action item complete after attending the required appointment(s).",
        execution_date: "13:00",
        always_send: false,
        days_before_due_to_remind: 0,
        reminder_repeat_interval_days: 0,
        destination: "luca+2025101902@docspera.com",
        reminder_type: "email",
      },
    ],
    assets: [],
    description_long: null,
    created_at: "",
    updated_at: "",
    execution_date: "2025-10-30T09:00:00Z",
  },
  {
    action_id: "0199fd03-bed9-7513-8129-c8d53298a1f1",
    enrollment_id: "0199fd03-63ab-776d-a7f0-ac685f2ed823",
    patient_id: "3428c4a8-5021-70d2-4502-1a1da2d0df6d",
    organization_id: "92bb0ce8-d16d-4837-a3e7-a3cb1b2568fa",
    execution_arn: "",
    schedule_arn: "",
    title:
      "Know when to stop taking blood thinners, over the counter medications, vitamins and supplements",
    description:
      "If you take any blood thinners, over the counter medications, vitamins or supplements, make sure you know when to stop taking them before surgery. If you are unsure, contact your surgeon's office. Your provider should instruct you on when to resume these medications.",
    delta_days_from_surgery: -12,
    uri: "velys://patientpath/orgs/92bb0ce8-d16d-4837-a3e7-a3cb1b2568fa/enrollments/0199fd03-63ab-776d-a7f0-ac685f2ed823/actions/0199fd03-bed9-7513-8129-c8d53298a1f1",
    slug: "ACTION",
    required: false,
    status: "pending",
    completed_at: null,
    reminders: [
      {
        message:
          "Ask your provider when to stop taking blood thinners and over the counter medications. When understood, mark this action item complete.",
        execution_date: "13:00",
        always_send: false,
        days_before_due_to_remind: 0,
        reminder_repeat_interval_days: 0,
        destination: "luca+2025101902@docspera.com",
        reminder_type: "email",
      },
    ],
    assets: [],
    description_long: null,
    created_at: "",
    updated_at: "",
    execution_date: "2025-11-21T09:00:00Z",
  },
  {
    action_id: "0199fd03-beda-7e42-81b3-d5388f98a3fc",
    enrollment_id: "0199fd03-63ab-776d-a7f0-ac685f2ed823",
    patient_id: "3428c4a8-5021-70d2-4502-1a1da2d0df6d",
    organization_id: "92bb0ce8-d16d-4837-a3e7-a3cb1b2568fa",
    execution_arn: "",
    schedule_arn: "",
    title:
      "Leave your surgical dressing in place unless otherwise instructed by your provider",
    description:
      "Leave your surgical dressing in place unless otherwise instructed by your provider. If you have any concerns, contact your provider's office or send an incision photo from the home screen.",
    delta_days_from_surgery: 3,
    uri: "velys://patientpath/orgs/92bb0ce8-d16d-4837-a3e7-a3cb1b2568fa/enrollments/0199fd03-63ab-776d-a7f0-ac685f2ed823/actions/0199fd03-beda-7e42-81b3-d5388f98a3fc",
    slug: "ACTION",
    required: false,
    status: "pending",
    completed_at: null,
    reminders: [
      {
        message:
          "Keep the surgical dressing in place unless your provider instructed otherwise. If concerned, contact your care team, otherwise mark this action item complete.",
        execution_date: "13:00",
        always_send: false,
        days_before_due_to_remind: 0,
        reminder_repeat_interval_days: 0,
        destination: "luca+2025101902@docspera.com",
        reminder_type: "email",
      },
    ],
    assets: [],
    description_long: null,
    created_at: "",
    updated_at: "",
    execution_date: "2025-12-06T09:00:00Z",
  },
  {
    action_id: "0199fd03-bedb-7d4e-8e5a-1378997d4d7a",
    enrollment_id: "0199fd03-63ab-776d-a7f0-ac685f2ed823",
    patient_id: "3428c4a8-5021-70d2-4502-1a1da2d0df6d",
    organization_id: "92bb0ce8-d16d-4837-a3e7-a3cb1b2568fa",
    execution_arn: "",
    schedule_arn: "",
    title: "Ask your provider when to resume medications",
    description:
      "Make sure you understand when you can resume any medications that were stopped prior to surgery. If you are unsure, check with the prescribing provider.",
    delta_days_from_surgery: 5,
    uri: "velys://patientpath/orgs/92bb0ce8-d16d-4837-a3e7-a3cb1b2568fa/enrollments/0199fd03-63ab-776d-a7f0-ac685f2ed823/actions/0199fd03-bedb-7d4e-8e5a-1378997d4d7a",
    slug: "ACTION",
    required: false,
    status: "pending",
    completed_at: null,
    reminders: [
      {
        message:
          "Ask your prescribing provider about when to resume any medications that were stopped prior to surgery. When understood, mark this action item complete.",
        execution_date: "11:00",
        always_send: false,
        days_before_due_to_remind: 0,
        reminder_repeat_interval_days: 0,
        destination: "luca+2025101902@docspera.com",
        reminder_type: "email",
      },
    ],
    assets: [],
    description_long: null,
    created_at: "",
    updated_at: "",
    execution_date: "2025-12-08T09:00:00Z",
  },
  {
    action_id: "0199fd03-bedc-7276-903a-44fd4d056108",
    enrollment_id: "0199fd03-63ab-776d-a7f0-ac685f2ed823",
    patient_id: "3428c4a8-5021-70d2-4502-1a1da2d0df6d",
    organization_id: "92bb0ce8-d16d-4837-a3e7-a3cb1b2568fa",
    execution_arn: "",
    schedule_arn: "",
    title:
      "Follow your provider's instructions on when to stop taking any medications prescribed after surgery",
    description:
      "Make sure you understand when you should stop taking any medications that were prescribed to you after surgery. If you are unsure, reach out to your provider's office.",
    delta_days_from_surgery: 30,
    uri: "velys://patientpath/orgs/92bb0ce8-d16d-4837-a3e7-a3cb1b2568fa/enrollments/0199fd03-63ab-776d-a7f0-ac685f2ed823/actions/0199fd03-bedc-7276-903a-44fd4d056108",
    slug: "ACTION",
    required: false,
    status: "pending",
    completed_at: null,
    reminders: [
      {
        message:
          "Make sure you know when to stop taking medications prescribed for your surgery. If unsure, ask your provider. When understood, mark this action item complete.",
        execution_date: "8:00",
        always_send: false,
        days_before_due_to_remind: 0,
        reminder_repeat_interval_days: 0,
        destination: "luca+2025101902@docspera.com",
        reminder_type: "email",
      },
    ],
    assets: [],
    description_long: null,
    created_at: "",
    updated_at: "",
    execution_date: "2026-01-02T09:00:00Z",
  },
];
